import { format, formatRelative, formatDistance, type Locale } from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import i18n from '@i18n/config';

const locales: Record<string, Locale> = {
  fr: fr,
  en: enCA,
};

/**
 * Gets the current date-fns locale based on i18n language
 */
function getCurrentLocale(): Locale {
  const lang = i18n.language?.split('-')[0] ?? 'fr';
  return locales[lang] ?? fr;
}

/**
 * Formats a date using the current i18n locale.
 * Common format strings:
 * - 'PP' - Oct 29, 2023 / 29 oct. 2023
 * - 'PPP' - October 29th, 2023 / 29 octobre 2023
 * - 'PPpp' - Oct 29, 2023, 12:00 AM / 29 oct. 2023 à 00:00
 * - 'Pp' - 10/29/2023, 12:00 AM / 29/10/2023 00:00
 *
 * @param date - Date to format (Date object or ISO string)
 * @param formatStr - date-fns format string (default: 'PP')
 * @returns Formatted date string
 */
export function formatDateLocale(date: Date | string, formatStr = 'PP'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, formatStr, { locale: getCurrentLocale() });
}

/**
 * Formats a date with time using the current i18n locale.
 *
 * @param date - Date to format (Date object or ISO string)
 * @param formatStr - date-fns format string (default: 'PPpp')
 * @returns Formatted date/time string
 */
export function formatDateTimeLocale(date: Date | string, formatStr = 'PPpp'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, formatStr, { locale: getCurrentLocale() });
}

/**
 * Formats a date relative to now (e.g., "yesterday at 12:00 PM")
 *
 * @param date - Date to format
 * @returns Relative date string
 */
export function formatDateRelative(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatRelative(dateObj, new Date(), { locale: getCurrentLocale() });
}

/**
 * Formats the distance between a date and now (e.g., "3 days ago")
 *
 * @param date - Date to compare
 * @param addSuffix - Whether to add "ago" or "in" suffix (default: true)
 * @returns Distance string
 */
export function formatDateDistance(date: Date | string, addSuffix = true): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatDistance(dateObj, new Date(), { locale: getCurrentLocale(), addSuffix });
}

/**
 * Sanitizes a date value by converting empty or whitespace-only strings to undefined.
 * This is useful for handling date fields that may come from the server or forms
 * in various formats that should be normalized.
 *
 * @param date - The date value to sanitize (can be any type at runtime)
 * @returns The original date string if valid, undefined otherwise
 */
export function sanitizeDateString(date: unknown): string | undefined {
  // Return undefined for null, undefined
  if (date == null) return undefined;
  
  // If it's a Date object, convert to ISO string
  if (date instanceof Date) {
    return date.toISOString().split('T')[0]; // Returns yyyy-MM-dd format
  }
  
  // Only process string values
  if (typeof date !== 'string') {
    return undefined;
  }
  
  // At this point, we've confirmed date is a string at runtime
  return date.trim() === '' ? undefined : date;
}

